import { ethers } from 'ethers';

/** Plain confidential amounts on-chain are bounded by uint32 in several code paths. */
export const CONFIDENTIAL_PLAIN_U32_MAX = 4294967295;

export function normalizeTokenDecimals(decimals) {
    const d = Number(decimals);
    if (!Number.isFinite(d) || d < 0) return 6;
    return Math.min(30, Math.floor(d));
}

/**
 * Truncate fractional part to at most `decimals` places (string-level; does not round up).
 */
export function truncateAmountInputString(valueStr, decimals) {
    const d = normalizeTokenDecimals(decimals);
    const s = String(valueStr ?? '').trim();
    if (!s || s === '-') return s;
    const neg = s.startsWith('-');
    const body = neg ? s.slice(1) : s;
    const parts = body.split('.');
    if (parts.length === 1) return (neg ? '-' : '') + body;
    const intPart = parts[0] || '0';
    const frac = (parts[1] || '').slice(0, d);
    return (neg ? '-' : '') + (frac.length > 0 ? `${intPart}.${frac}` : intPart);
}

export function parseUserAmountToPlainBn(amountStr, decimals) {
    const d = normalizeTokenDecimals(decimals);
    const t = truncateAmountInputString(amountStr, d);
    if (!t || t === '-') return ethers.BigNumber.from(0);
    try {
        let bn = ethers.utils.parseUnits(t, d);
        const cap = ethers.BigNumber.from(String(CONFIDENTIAL_PLAIN_U32_MAX));
        if (bn.gt(cap)) bn = cap;
        return bn;
    } catch {
        return ethers.BigNumber.from(0);
    }
}

export function parseUserAmountToPlainInt(amountStr, decimals) {
    return parseUserAmountToPlainBn(amountStr, decimals).toNumber();
}

/**
 * Parse user decimal string to plain units; fails if above u32 max or invalid.
 * @returns {{ ok: true, bn: import('ethers').BigNumber } | { ok: false, reason: 'empty'|'parse'|'max' }}
 */
export function parseUserAmountToPlainBnStrict(amountStr, decimals) {
    const d = normalizeTokenDecimals(decimals);
    const t = truncateAmountInputString(amountStr, d);
    if (!t || t === '-') return { ok: false, reason: 'empty' };
    try {
        const bn = ethers.utils.parseUnits(t, d);
        const cap = ethers.BigNumber.from(String(CONFIDENTIAL_PLAIN_U32_MAX));
        if (bn.gt(cap)) return { ok: false, reason: 'max' };
        return { ok: true, bn };
    } catch {
        return { ok: false, reason: 'parse' };
    }
}

export function plainToUserAmountNumber(plain, decimals) {
    const d = normalizeTokenDecimals(decimals);
    const bn = ethers.BigNumber.from(plain == null ? '0' : String(plain));
    return parseFloat(ethers.utils.formatUnits(bn, d));
}

export function formatPlainForDisplay(plain, decimals) {
    const d = normalizeTokenDecimals(decimals);
    const bn = ethers.BigNumber.from(plain == null ? '0' : String(plain));
    let s = ethers.utils.formatUnits(bn, d);
    if (s.includes('.')) s = s.replace(/\.?0+$/, '');
    return s || '0';
}

export function maxUserFacingAmountFromPlainCap(decimals) {
    const d = normalizeTokenDecimals(decimals);
    return CONFIDENTIAL_PLAIN_U32_MAX / 10 ** d;
}
